import { useCallback, useEffect, useState } from "react";
import {
  CONSTIPATION_COURSE_KEY,
  PROTEIN_COURSE_KEY,
  PROTEIN_GRAMS_PER_KG_DEFAULT,
  PROTEIN_GRAMS_PER_KG_MAX,
  type CourseRecommendationReason,
  type CourseRecommendationRow,
  type EmployeeProteinTarget,
  type ProteinAssessmentCause,
  type RecommendableCourseKey,
} from "@care-by-ella/contracts";
import { useAuth } from "@/auth/AuthContext";
import { UnauthorizedError } from "@/lib/clinician-api";
import {
  getProteinTarget,
  listCourseRecommendations,
  recommendCourse,
  setProteinTarget,
} from "@/lib/nutrition-courses-api";
import { useReadOnly } from "./SupportView";

// TES-1067 — the two "Stöd → Kost" courses on the patient page:
//   - "när coachen aktivt rekommenderar kursen": the coach (or doctor)
//     recommends either course; the list shows what was recommended, why,
//     and whether she dismissed it;
//   - the protein target as she sees it, and the doctor's three settings
//     behind it: a reference weight that wins over her goal weight, an
//     individually assessed g/kg (1.2–1.6), and a mark that the target
//     needs an individual assessment, for a condition F-02 cannot see.
// Staff-facing labels; the patient never sees this panel.

const COURSE_LABELS: Record<RecommendableCourseKey, string> = {
  [PROTEIN_COURSE_KEY]: "Protein och muskelmassa under medicinsk viktminskning",
  [CONSTIPATION_COURSE_KEY]: "Förstoppning under medicinsk viktminskning",
};

const REASON_LABELS: Record<CourseRecommendationReason, string> = {
  treatment_start: "Behandlingsstart",
  constipation_reported: "Förstoppning i incheckning",
  coach: "Rekommenderad av vårdteamet",
};

const CAUSE_LABELS: Record<ProteinAssessmentCause, string> = {
  kidney: "njursjukdom (F-02)",
  liver: "leversjukdom (F-02)",
  care_team: "markerat av läkare",
};

const dateOf = (iso: string) => new Date(iso).toLocaleDateString("sv-SE");
const num = (n: number) => n.toLocaleString("sv-SE");

export function PatientNutritionCourses({ patientId }: { patientId: string }) {
  const { claims, signOut } = useAuth();
  const readOnly = useReadOnly();
  const canRecommend = !readOnly && claims?.role !== "admin";
  const canSetTarget = !readOnly && claims?.role === "doctor";

  const [recs, setRecs] = useState<CourseRecommendationRow[] | null>(null);
  const [target, setTarget] = useState<EmployeeProteinTarget | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [refKg, setRefKg] = useState("");
  const [gPerKg, setGPerKg] = useState("");
  const [assess, setAssess] = useState(false);

  const handleError = useCallback(
    (e: unknown) => {
      if (e instanceof UnauthorizedError) {
        void signOut();
        return;
      }
      setError(e instanceof Error ? e.message : String(e));
    },
    [signOut],
  );

  const applyTarget = useCallback((t: EmployeeProteinTarget) => {
    setTarget(t);
    setRefKg(t.setting?.referenceWeightKg?.toString() ?? "");
    setGPerKg(t.setting?.gramsPerKg?.toString() ?? "");
    setAssess(t.setting?.individualAssessmentRequired ?? false);
  }, []);

  useEffect(() => {
    listCourseRecommendations(patientId).then(setRecs).catch(handleError);
    getProteinTarget(patientId).then(applyTarget).catch(handleError);
  }, [patientId, handleError, applyTarget]);

  async function run(fn: () => Promise<void>) {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (e) {
      handleError(e);
    } finally {
      setBusy(false);
    }
  }

  const onRecommend = (courseKey: RecommendableCourseKey) =>
    run(async () => setRecs(await recommendCourse(patientId, courseKey)));

  const onSaveTarget = () =>
    run(async () => {
      const kg = refKg.trim() ? Number(refKg.replace(",", ".")) : null;
      const factor = gPerKg.trim() ? Number(gPerKg.replace(",", ".")) : null;
      applyTarget(
        await setProteinTarget(patientId, {
          referenceWeightKg: kg,
          gramsPerKg: factor,
          individualAssessmentRequired: assess,
        }),
      );
    });

  const t = target?.target;

  return (
    <section className="bg-surface border border-surfaceMuted rounded-md p-md flex flex-col gap-md">
      <h2 className="font-display text-lg">Kostkurser</h2>

      {error ? (
        <div className="bg-danger/10 border border-danger/30 text-danger rounded-md p-sm text-sm">
          {error}
        </div>
      ) : null}

      <div className="flex flex-col gap-xs text-sm">
        <h3 className="font-medium">Proteinmål</h3>
        {!t ? (
          <p className="text-textSecondary">Hämtar…</p>
        ) : t.status === "computed" ? (
          <p data-testid="protein-target-computed">
            cirka {t.gramsPerDay} g protein per dag ({num(t.gramsPerKg)} g/kg ×{" "}
            {num(t.referenceWeightKg)} kg,{" "}
            {t.referenceSource === "care_team"
              ? "referensvikt satt av vårdteamet"
              : "registrerad målvikt"}
            )
          </p>
        ) : t.status === "needs_assessment" ? (
          <p data-testid="protein-target-assess">
            Personligt proteinmål behöver bedömas —{" "}
            {t.causes.map((c) => CAUSE_LABELS[c]).join(", ")}. Inget gramvärde
            visas för patienten.
          </p>
        ) : (
          <p data-testid="protein-target-none" className="text-textSecondary">
            Ingen målvikt och ingen referensvikt — inget gramvärde visas.
            Aktuell vikt används aldrig.
          </p>
        )}
        {target ? (
          <p className="text-xs text-textSecondary">
            Registrerad målvikt:{" "}
            {target.goalWeightKg != null
              ? `${num(target.goalWeightKg)} kg`
              : "—"}
            {target.setting
              ? ` · senast ändrat ${dateOf(target.setting.createdAt)}`
              : null}
          </p>
        ) : null}

        {canSetTarget && target ? (
          <div className="flex flex-wrap items-end gap-sm mt-xs">
            <label className="flex flex-col text-xs">
              Referensvikt (kg)
              <input
                className="border border-surfaceMuted rounded-sm px-xs py-[2px] w-24"
                inputMode="decimal"
                value={refKg}
                onChange={(e) => setRefKg(e.target.value)}
                placeholder="målvikt"
              />
            </label>
            <label className="flex flex-col text-xs">
              Individuellt g/kg ({num(PROTEIN_GRAMS_PER_KG_DEFAULT)}–
              {num(PROTEIN_GRAMS_PER_KG_MAX)})
              <input
                className="border border-surfaceMuted rounded-sm px-xs py-[2px] w-24"
                inputMode="decimal"
                value={gPerKg}
                onChange={(e) => setGPerKg(e.target.value)}
                placeholder={num(PROTEIN_GRAMS_PER_KG_DEFAULT)}
              />
            </label>
            <label className="flex items-center gap-xs text-xs">
              <input
                type="checkbox"
                checked={assess}
                onChange={(e) => setAssess(e.target.checked)}
              />
              Kräver individuell bedömning
            </label>
            <button
              type="button"
              onClick={() => void onSaveTarget()}
              disabled={busy}
              className="text-xs text-brand hover:opacity-80 disabled:opacity-50"
            >
              Spara
            </button>
          </div>
        ) : null}
      </div>

      <div className="flex flex-col gap-xs text-sm">
        <h3 className="font-medium">Rekommendationer</h3>
        {!recs ? (
          <p className="text-textSecondary">Hämtar…</p>
        ) : recs.length === 0 ? (
          <p className="text-textSecondary">Inga rekommendationer ännu.</p>
        ) : (
          <ul className="flex flex-col gap-[2px]">
            {recs.map((r) => (
              <li key={r.id} className="text-xs">
                {COURSE_LABELS[r.courseKey]} · {REASON_LABELS[r.reason]} ·{" "}
                {dateOf(r.createdAt)}
                {r.dismissedAt
                  ? ` · dold av patienten ${dateOf(r.dismissedAt)}`
                  : null}
              </li>
            ))}
          </ul>
        )}
        {canRecommend ? (
          <div className="flex flex-wrap gap-sm mt-xs">
            {(Object.keys(COURSE_LABELS) as RecommendableCourseKey[]).map(
              (k) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => void onRecommend(k)}
                  disabled={busy}
                  className="text-xs text-brand hover:opacity-80 disabled:opacity-50"
                >
                  Rekommendera: {COURSE_LABELS[k]}
                </button>
              ),
            )}
          </div>
        ) : null}
      </div>
    </section>
  );
}
